/**
 * Pure resolution of the two "where does this app point" values that differ
 * between the browser build and the Capacitor-shell build of the SAME
 * `dist/` bundle:
 *
 *   1. {@link resolveApiBaseUrl}    — the `apps/api` origin every tRPC and
 *                                     `/oauth/*` call targets.
 *   2. {@link resolveOAuthResource} — the RFC 8707 `resource` indicator sent
 *                                     on `GET /oauth/authorize`, which becomes
 *                                     the minted token's `audience`.
 *
 * Both are pure functions over an explicit {@link RuntimeTarget} so they are
 * unit-testable without `import.meta.env`, `window`, or a Capacitor bridge
 * (CLAUDE.md § "Component Testability" — logic in a `.ts`, wiring at the call
 * site). `lib/trpc.ts` and `lib/auth/oauth_pkce.ts` are the only two callers
 * that build a real {@link RuntimeTarget}.
 *
 * ── Why the shell can't just reuse the web resolution ────────────────────────
 *
 * `apps/wagepro-mobile/capacitor.config.ts` serves the bundle from
 * `http://localhost` (no port). That single fact breaks BOTH values:
 *
 *   - API URL: the old dev resolution was `http://${window.location.hostname}
 *     :8787`, i.e. `http://localhost:8787` inside the shell. Capacitor's
 *     `WebViewLocalServer.isMainUrl()` compares hosts with NO port component,
 *     so that request is indistinguishable from the app's own origin and is
 *     served out of the APK asset bundle instead of hitting the network — the
 *     dev API is simply unreachable. The Android emulator's alias for the host
 *     machine's loopback ({@link ANDROID_EMULATOR_LOOPBACK_HOST}) is a
 *     different host, so it routes to the network correctly. This is the same
 *     reasoning already recorded on `capacitor.config.ts`'s `allowNavigation`.
 *
 *   - OAuth resource: `oauth_pkce.ts` used to send `resource = origin`, which
 *     in the shell is `http://localhost` — a value matching NO configured
 *     audience slot (`env.WAGEPRO_OAUTH_AUDIENCE` is `http://localhost:3013`
 *     locally, per `apps/api/.dev.vars.example`). `resolveAcceptedAudience`
 *     (`apps/api/src/lib/oauth_audience.ts`) would resolve that token to no
 *     slot at all and every subsequent call — including the `auth.identity`
 *     one `AuthGate` boots on — comes back FORBIDDEN. That is the exact
 *     silent-infinite-redirect failure `oauth_pkce.ts`'s module docblock
 *     records as the known risk for whoever wires up mobile sign-in (#22019).
 *
 * The fix for the second one is to stop conflating two different things that
 * merely happened to be equal on the web. `redirect_uri` must stay the REAL
 * WebView origin (that is where the browser actually lands, and it is
 * exact-string matched against the client's registered URIs). `resource` is
 * an app IDENTITY, and the shell's identity is WagePro — the same app, the
 * same audience slot, the same token — so it sends the app's canonical
 * origin, not the origin its assets happen to be served from.
 */

/** Production `apps/api` origin. Matches `lib/trpc.ts`'s pre-existing default. */
export const PRODUCTION_API_URL = 'https://api.ledgerpro.ai';

/**
 * Canonical production origin of the WagePro app — the value a production
 * `env.WAGEPRO_OAUTH_AUDIENCE` is expected to hold, and the redirect origin
 * registered for `first_party_wagepro_web` in
 * `apps/api/migrations/20260720000000_wagepro_oauth_client.sql`.
 */
export const PRODUCTION_APP_ORIGIN = 'https://app.wagepro.ai';

/**
 * Canonical local-dev origin of the WagePro app: wagepro-web's pinned dev
 * port (`package.json`'s `dev` script, `--strictPort 3013`). This is what
 * `apps/api/.dev.vars.example` sets `WAGEPRO_OAUTH_AUDIENCE` to, so a shell
 * build pointed at a local API must send exactly this as its `resource`.
 */
export const DEV_APP_ORIGIN = 'http://localhost:3013';

/** Port `apps/api`'s `wrangler dev` listens on. */
export const DEV_API_PORT = 8787;

/**
 * The Android emulator's alias for the host machine's loopback interface.
 * Reaching the dev API from inside the emulator goes through this host, never
 * `localhost` — see the module docblock.
 */
export const ANDROID_EMULATOR_LOOPBACK_HOST = '10.0.2.2';

/** Everything the two resolvers below need to know about the current runtime. */
export interface RuntimeTarget {
  /** `import.meta.env.VITE_API_URL` — an explicit override always wins. */
  explicitApiUrl?: string | undefined;
  /**
   * `import.meta.env.VITE_WAGEPRO_APP_ORIGIN` — an explicit override for the
   * OAuth `resource` indicator. Set this on a shell build whose API target
   * is neither the local dev server nor production (a staging/devbox API with
   * its own `WAGEPRO_OAUTH_AUDIENCE`), so pointing the app somewhere new is a
   * build-config change rather than a code change.
   */
  explicitAppOrigin?: string | undefined;
  /**
   * `import.meta.env.DEV`.
   *
   * NOTE for the Capacitor shell: this is ALWAYS `false` in a bundled APK.
   * The shell wraps `dist/`, and `vite build` produces `DEV=false` regardless
   * of `--mode development` or `NODE_ENV=development` (verified empirically —
   * `dev_auth_bypass.ts` is dead-code-eliminated from all three variants). The
   * `dev` branches below are therefore reached only when a live Vite dev
   * server serves the bundle: the plain web app, or a Capacitor live-reload
   * setup pointing `server.url` at it. A bundled APK that must talk to a
   * non-production API uses `explicitApiUrl`/`explicitAppOrigin` instead —
   * that is what `apps/wagepro-mobile`'s `:local` scripts set. See
   * `docs/ops-wagepro-mobile-shell.md`.
   */
  dev: boolean;
  /** Running inside a native Capacitor shell (`lib/platform.ts`'s `isNative`). */
  native: boolean;
  /** Running inside the ANDROID shell specifically (`lib/platform.ts`'s `isAndroid`). */
  android: boolean;
  /** `window.location.hostname`, for the unchanged dev-in-a-browser behaviour. */
  browserHostname: string;
}

/**
 * The `apps/api` base URL this runtime should target.
 *
 * Precedence: explicit `VITE_API_URL` → production default (non-dev builds) →
 * the Android-emulator loopback alias (dev, Android shell) → `localhost`
 * (dev, iOS shell — the simulator shares the host's loopback, and the iOS
 * WebView origin is a custom scheme (`wagepro://localhost` as of 2026-08-25,
 * previously Capacitor's default `capacitor://localhost` — either way, a
 * different SCHEME from `http`), so there is no asset-server interception to
 * route around) → the browsing hostname
 * (dev, plain browser — unchanged behaviour, and deliberately the hostname
 * rather than a hardcoded `localhost` so a phone hitting a dev server over
 * the LAN reaches the same machine's API).
 */
export function resolveApiBaseUrl(target: RuntimeTarget): string {
  if (target.explicitApiUrl) return target.explicitApiUrl;
  if (!target.dev) return PRODUCTION_API_URL;
  // The native branches below describe a shell serving the bundle from its OWN
  // asset server, whose origin is always `http://localhost` (see
  // `apps/wagepro-mobile/capacitor.config.ts`). A shell pointed at a REMOTE
  // dev server (`server.url`, i.e. `WAGEPRO_SHELL_DEV_SERVER`) is a different
  // situation entirely: the page came over the network from a real host, and
  // that host is where its API lives too.
  //
  // Without this guard a physical phone loading `http://192.168.1.2:3013`
  // resolved its API to `http://10.0.2.2:8787` — the EMULATOR's alias for its
  // host's loopback, which addresses nothing at all on a real device. The
  // hostname branch at the bottom already handles both remote cases correctly,
  // so fall through to it whenever the page was not served from localhost.
  const servedByShellAssetServer = target.browserHostname === 'localhost';
  if (servedByShellAssetServer) {
    if (target.android) {
      return `http://${ANDROID_EMULATOR_LOOPBACK_HOST}:${String(DEV_API_PORT)}`;
    }
    if (target.native) return `http://localhost:${String(DEV_API_PORT)}`;
  }
  return `http://${target.browserHostname}:${String(DEV_API_PORT)}`;
}

/**
 * The RFC 8707 `resource` indicator to send on `GET /oauth/authorize` — the
 * value the API stamps as the minted token's `audience`, and which
 * `resolveAcceptedAudience` matches against `env.WAGEPRO_OAUTH_AUDIENCE`.
 *
 * On the web this is `windowOrigin`, byte-identical to the pre-existing
 * behaviour: the browser origin IS the app's identity, and a dev browser on
 * `:3013` already matches the seeded dev audience.
 *
 * In the shell the served origin (`http://localhost`) is an implementation
 * detail of Capacitor's asset server and matches no audience slot, so the
 * app's CANONICAL origin is sent instead — dev or production, mirroring
 * {@link resolveApiBaseUrl}'s own split so the two can never disagree about
 * which environment they are pointed at. `explicitAppOrigin` overrides both.
 *
 * Note this is deliberately NOT the value used to build `redirect_uri` — see
 * the module docblock for why those two must stay separate.
 */
export function resolveOAuthResource(
  target: RuntimeTarget,
  windowOrigin: string,
): string {
  if (target.explicitAppOrigin) return target.explicitAppOrigin;
  if (!target.native) return windowOrigin;
  return target.dev ? DEV_APP_ORIGIN : PRODUCTION_APP_ORIGIN;
}
